import moment from 'moment';
import React, { Component } from 'react';
import { Helmet } from 'react-helmet-async';
import { connect } from 'react-redux';
import { Redirect, RouteComponentProps, withRouter } from 'react-router-dom';
import * as api from '../../../api/tv';
import NoSupport from '../../../components/NoSupport';
import Player, { PlayerEvent } from '../../../components/Player';
import HuaweiError from '../../../controllers/HuaweiErrors';
import { ScriptService } from '../../../controllers/ScriptService';
import i18n from '../../../i18n';
import {
  AlertData,
  AlertType,
  ApiAuthResponse,
  AuthReducerState,
  Channel,
  LegacyBookmark,
  NielsenChannelMapping,
  Program,
  RootState,
  StreamData,
  StreamType,
  StreamTypeNumber,
  StreamTypeObject,
} from '../../../interfaces';
import { wrapAssetAsAltiboxAsset } from '../../../utils/altiboxassetUtils';
import { addScalecastParams, isApplicableForAdditionalConcurrency } from '../../../utils/tvUtils';
import channelPermissions from '../../../utils/channelPermissions';
import { showAlert } from '../../app/actions';
import { loadAllUsersBookmarks } from '../../app/actions/index';
import { hideChannelList, showChannelList, ShowChannelListAction, unloadCurrentLiveProgram } from '../actions';
import ChannelLocked from '../ChannelLocked';
import './style.scss';
import { noop } from 'lodash';
import { ChannelConcurrencyGroup } from '../../../queries/config/types';

interface Props extends RouteComponentProps {
  channels: Channel[];
  channelConcurrencyGroup?: ChannelConcurrencyGroup[];
  isHome: boolean;
  currentChannel: Channel | undefined;
  currentProgram: Program | undefined;
  startingCatchup: boolean;
  bookmarks: LegacyBookmark[];
  shouldHideControls: boolean;
  shouldHideCursor: boolean;
  currentChannelIsLocked: boolean;
  isGuest: boolean;
  auth: ApiAuthResponse | undefined;
  authStatus: AuthReducerState;
  overlay?: JSX.Element | null;
  loadBookmarks: () => Promise<LegacyBookmark[]>;
  hideChannelList: () => void;
  showChannelList: () => ShowChannelListAction;
  updateCurrentLiveProgram: () => void;
  unloadCurrentLiveProgram: () => void;
  showAlert: (alert: AlertData) => {
    type: symbol;
    alertVisible: boolean;
    alert: { title: string; text: string[] };
  };
  handleKeyDown?: (_: KeyboardEvent) => void;
  nielsenChannelMapping?: NielsenChannelMapping[];
  unmountPlayer: () => void;
}

interface State {
  stream: StreamTypeObject | undefined;
  catchupDonePlaying: boolean;
  redirectIn: number | undefined;
}

class PlayerStreamManager extends Component<Props, State> {
  state: State = {
    stream: undefined,
    catchupDonePlaying: false,
    redirectIn: undefined,
  };

  // Strict Class Initialization ignored
  redirectInterval!: NodeJS.Timer;

  sessionId: string | undefined;

  componentDidMount() {
    this.setStream(this.props);
  }
  async setStream({ currentProgram, currentChannel, channelConcurrencyGroup, channels, authStatus }: Props) {
    let channelMediaId = '';
    if (currentChannel === undefined) {
      return;
    }

    if (currentProgram === undefined) {
      return;
    }

    if (currentChannel && currentChannel.mediaId) {
      channelMediaId = currentChannel.mediaId;
    } else if (currentProgram && currentProgram.recordedMediaIds && currentProgram.recordedMediaIds.length > 0) {
      channelMediaId = currentProgram.recordedMediaIds[0];
    }

    try {
      // The current channel is the same as the report to -> do nothing except report to reportTo channel /Play
      if (
        channelConcurrencyGroup &&
        ScriptService.isDKUser() &&
        isApplicableForAdditionalConcurrency(currentChannel.contentId, channelConcurrencyGroup) &&
        !authStatus.isHome
      ) {
        const concurrencyContentId = channelConcurrencyGroup.find((concurrencyGroup) =>
          concurrencyGroup.channelIds.includes(currentChannel.contentId),
        )?.reportTo;

        const mediaIdToPlay = channels.find((channel) => channel.contentId === concurrencyContentId)?.mediaId;
        if (concurrencyContentId && mediaIdToPlay) {
          await api.playChannelGroup({
            contentid: concurrencyContentId,
            mediaid: mediaIdToPlay,
            profileId: -1,
            playtype: 2,
          });
        }
      }

      const tvChannelResult = await this.startTvChannel(
        currentChannel.contentId,
        currentChannel.chanNo,
        currentProgram,
        channelMediaId,
      );

      return tvChannelResult;
    } catch (error) {
      if (error.retcode === HuaweiError.MAX_PLAYBACK_SESSIONS) {
        this.notifyToManyStreams();
        this.props.history.goBack();
      }
    }
  }

  componentWillReceiveProps(nextProps: Props) {
    if (
      nextProps.currentChannel !== this.props.currentChannel ||
      nextProps.currentProgram !== this.props.currentProgram
    ) {
      this.setStream(nextProps);
    }
  }

  componentWillUnmount() {
    this.props.unloadCurrentLiveProgram();
  }

  async startTvChannel(
    channelId: string,
    chanNo: string,
    program: Program,
    mediaId: string,
    channelGroup?: ChannelConcurrencyGroup,
  ) {
    if (ScriptService.isCasting()) {
      this.startNewStream(undefined, channelId, chanNo, program, mediaId);
      return;
    }
    return api.getChannelLivePlayData(channelId, mediaId).then((playData) => {
      if (!this.props.currentChannelIsLocked) {
        if (playData && !ScriptService._isSafari()) {
          playData = addScalecastParams(playData, this.props.currentProgram);
        }
        this.startNewStream(playData, channelId, chanNo, program, mediaId);
      }

      return playData;
    });
  }

  notifyToManyStreams() {
    this.alert(i18n.t<string>('concurrency title'), [
      i18n.t<string>('concurrency message1'),
      i18n.t<string>('concurrency message2'),
    ]);
    this.props.showChannelList();
  }

  getCatchupBookmark(contentId: string) {
    return this.props.bookmarks.find(
      (bookmark) => bookmark.bookmarkType === StreamTypeNumber.CATCHUP && bookmark.contentId === contentId,
    );
  }

  startNewStream(
    playData: StreamData | undefined,
    channelId: string,
    chanNo: string,
    program: Program,
    mediaId: string,
  ) {
    clearInterval(this.redirectInterval);
    let startBookmark: number | undefined = undefined;

    const search = new URLSearchParams(this.props.location.search);
    const position = search.get('position');

    if (position !== null) {
      const now = moment();
      startBookmark = moment(program.starttime).diff(now, 'seconds') + parseInt(position, 10);
    }

    let theNewState = {
      redirectIn: undefined,
      catchupDonePlaying: false,
      stream: {
        isGuest: this.props.isGuest,
        streamType: StreamType.LIVE,
        picture: program.picture,
        channelId: program.channelid,
        programStart: program.starttime,
        programEnd: program.endtime,
        concurrencyMediaId: '',
        mediaId: mediaId,
        contentId: channelId,
        concurrencyContenId: '',
        chanNo: chanNo,
        startBookmark: startBookmark,
        playData: {
          manifestUrl: playData?.manifestUrl,
          licenseUrl: playData?.licenseUrl,
          customData: playData?.customData,
        },
      } as StreamTypeObject,
    };

    this.setState(theNewState);
    // React doesn't notice the changes in the nested objects in stream, so we have to tell it to rerender.
    this.forceUpdate();
  }

  alert = (title: string, message: string[]) => {
    this.props.showAlert({
      title: title,
      text: message,
      type: AlertType.INFO,
    });
  };

  updateRedirect = () => {
    if (this.state.redirectIn !== undefined && this.state.redirectIn <= 0) {
      this.goToLive();
    } else if (this.state.redirectIn && this.state.redirectIn > 0) {
      this.setState({ redirectIn: this.state.redirectIn - 1 });
    }
  };

  programEnded = ({ streamType }: PlayerEvent) => {
    clearInterval(this.redirectInterval);
    if (streamType === 'live') {
      this.props.updateCurrentLiveProgram();
    } else {
      // catchup
      this.setState({ redirectIn: 15 });
      this.redirectInterval = setInterval(this.updateRedirect, 1000);
    }
  };

  goToLive = () => {
    clearInterval(this.redirectInterval);
    this.setState({
      redirectIn: undefined,
      catchupDonePlaying: true,
      stream: undefined,
    });
  };

  cancelRedirect = () => {
    clearInterval(this.redirectInterval);
    this.setState({
      redirectIn: undefined,
    });
  };

  renderRedirectSoon() {
    return (
      <div className="redirect-soon aib-modal" onClick={this.props.hideChannelList}>
        <p>
          {i18n.t<string>('program done')}.<br />
          {i18n.t<string>('going to live tv in')}&nbsp;
          <span className="now">{this.state.redirectIn}</span>&nbsp;
          {i18n.t<string>('seconds')}.
        </p>
        <div className="button-col">
          <button className="button-next" onClick={this.goToLive}>
            <span className="aib-icon">z</span>
            {i18n.t<string>('go to live now')}
          </button>
          <button className="button-cancel" onClick={this.cancelRedirect}>
            <span className="aib-icon">B</span>
            {i18n.t<string>('cancel')}
          </button>
        </div>
      </div>
    );
  }

  onControlsClick = () => {
    this.cancelRedirect();
  };

  dismiss() {
    this.props.unmountPlayer();
  }

  render() {
    if (!ScriptService.isCurrentBrowserSupported()) {
      return <NoSupport />;
    }

    if (this.props.startingCatchup && this.state.catchupDonePlaying) {
      return <Redirect to={'/tv/' + this.props.currentChannel!.contentId} />;
    }

    if (this.props.isGuest || this.props.currentChannelIsLocked) {
      return (
        <ChannelLocked
          currentChannel={this.props.currentChannel}
          currentProgram={this.props.currentProgram}
          onMobile={ScriptService.onMobile()}
          canLogin={this.props.isGuest || this.props.currentChannelIsLocked}
        />
      );
    }
    const permissions = channelPermissions(this.props.currentChannel, this.props.authStatus);

    if (permissions.geoLocked) {
      return (
        <ChannelLocked
          currentChannel={this.props.currentChannel}
          currentProgram={this.props.currentProgram}
          onMobile={ScriptService.onMobile()}
          canLogin={this.props.isGuest || this.props.currentChannelIsLocked}
        />
      );
    }

    const pageTitle = this.props.currentChannel ? this.props.currentChannel.name : 'Altibox TV';
    return (
      <React.Fragment>
        {this.state.redirectIn ? this.renderRedirectSoon() : null}
        <Helmet>
          <title>{pageTitle}</title>
          <meta property="og:site_name" content={'Altibox TV'} />
          <meta property="og:title" content={pageTitle} />
          <meta property="og:url" content={window.location.href} />
          <meta
            property="og:image"
            content={
              this.props.currentChannel && this.props.currentChannel.pictures
                ? this.props.currentChannel.pictures[0].href
                : ''
            }
          />
          <meta property="og:description" content={'Altibox TV - ' + pageTitle} />
        </Helmet>

        {this.state.stream && (
          <Player
            concurrencyGroupChannels={this.props.channelConcurrencyGroup}
            stream={this.state.stream}
            currentAsset={this.props.currentProgram ? wrapAssetAsAltiboxAsset(this.props.currentProgram) : undefined}
            currentProgram={this.props.currentProgram}
            onPlayerClick={this.props.hideChannelList}
            onControlsClick={this.onControlsClick}
            onEnded={this.programEnded}
            onBookmark={() => {
              // we don't bookmark live tv
            }}
            onConcurrencyError={noop}
            onPlayerError={noop}
            hideControls={this.props.shouldHideControls || this.props.overlay !== null}
            hideCursor={this.props.shouldHideCursor}
            onAlert={this.alert}
            popupOverlay={this.props.overlay}
            handleKeyDown={this.props.handleKeyDown}
            nielsenChannelMapping={this.props.nielsenChannelMapping}
          />
        )}
      </React.Fragment>
    );
  }
}

export default connect(
  (state: RootState) => ({
    isHome: state.authReducer.isHome,
    startingCatchup: state.channelsReducer.startingCatchup,
    shouldHideControls: state.app.shouldFadeOut || state.channelsReducer.channelListVisible,
    shouldHideCursor: !state.channelsReducer.channelListVisible && state.app.shouldFadeOut,
    isGuest: state.authReducer.isGuest,
    bookmarks: state.app.bookmarks,
    auth: state.authReducer.auth,
    authStatus: state.authReducer,
    nielsenChannelMapping: state.app.nielsenChannelMapping,
  }),
  (dispatch) => ({
    loadBookmarks: () => dispatch(loadAllUsersBookmarks()),
    hideChannelList: () => {
      dispatch(hideChannelList());
    },
    showChannelList: () => dispatch(showChannelList()),
    unloadCurrentLiveProgram: () => dispatch(unloadCurrentLiveProgram()),
    showAlert: (alert: AlertData) => dispatch(showAlert(alert)),
  }),
)(withRouter(PlayerStreamManager));
